import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { gatorProtectActions, portalModalsActions } from '@/redux/modules';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from '@/pages/GatorProtect/GatorProtectHandler/GatorProtect.enum';
import { resetPortalModalsGlobals } from './gatorProtectPortalModals.shared';
import type {
  UseGatorProtectGlobalParams,
  UseGatorProtectGlobalReturn,
} from './useGatorProtectGlobal.types';

/**
 * Header-wide side effects shared across Gator Protect modals (portal modals fetch, plans list, PRO price).
 * Modal hooks stay focused on visibility rules and UI callbacks.
 */
export function useGatorProtectGlobal({
  summary,
  isHome,
  resetModalsVisibility,
  isInvoicesPage,
}: UseGatorProtectGlobalParams): UseGatorProtectGlobalReturn {
  const dispatch = useDispatch();
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;

  const resetPortalModalsAndVisibility = useCallback(() => {
    resetPortalModalsGlobals(dispatch, resetModalsVisibility);
  }, [dispatch, resetModalsVisibility]);

  const shouldFetchPortalModals = useCallback(() => {
    if (gatorProtectEnabled) {
      if (isHome || isInvoicesPage) {
        return true;
      }
    }
    resetPortalModalsAndVisibility();
    return false;
  }, [isHome, gatorProtectEnabled, resetPortalModalsAndVisibility, isInvoicesPage]);

  const fetchModals = useCallback(() => {
    if (shouldFetchPortalModals()) {
      dispatch(portalModalsActions.fetch.request());
    }
  }, [dispatch, shouldFetchPortalModals]);

  const getGatorProtectPlans = useCallback(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  const getGatorProtectProProduct = useCallback(() => {
    if (!gatorProtectEnabled) {
      return;
    }
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch, gatorProtectEnabled]);

  return {
    fetchModals,
    getGatorProtectPlans,
    getGatorProtectProProduct,
  };
}
